import React, { FC, useRef, useEffect } from 'react'
import { gsap } from 'gsap/all'
import { createShowFromBottomAnimation } from 'src/helpers/animations'
import StyledBulletList from './StyledBulletList'
import {
  ISectionHeaderProps,
  ISectionListItem,
  ArrowRightIcon,
  SectionHeaderTitle,
  SectionHeaderDescription,
  SectionHeaderActions,
  SectionHeaderLinkCta,
  SectionGridCards,
  SectionInteractiveRows,
} from './section-header'

export { ArrowRightIcon }
export type { ISectionListItem, ISectionHeaderProps }

export const SectionHeader: FC<ISectionHeaderProps> & {
  Title: typeof SectionHeaderTitle
  Description: typeof SectionHeaderDescription
  Actions: typeof SectionHeaderActions
  GridCards: typeof SectionGridCards
  InteractiveRows: typeof SectionInteractiveRows
} = ({
  subtitle,
  titlePart1,
  titlePart2,
  titlePart3,
  description,
  descriptionVariant = 'highlight',
  ctaText,
  ctaLink,
  ctaHoverText,
  ctaSize,
  cta2Text,
  cta2Link,
  cta2HoverText,
  cta2Size,
  ctaVariant = 'button',
  linkText,
  linkUrl,
  titleOnly = false,
  contentOnly = false,
  isH1 = false,
  center = false,
  className = '',
  descriptionClassName = '',
  descriptionWidthClass = 'md:w-6/12',
  isFullWidthDescription = false,
  theme = 'light',
  subDescription,
  subDescriptionClassName = '',
  listItems,
  gridItems,
  isGridList = false,
  listVariant,
  listLayout,
  interactiveRowsVariant,
  interactiveRowsSize,
  isFullWidthSection = false,
  isSplitLayout = false,
  hasImage = false,
  stackActions = false,
  hideGridMarker,
  gridTextSize,
  centerGridText,
  disableHoverOnMobile,
  disableGridHover,
  disableBalance = false,
  onCtaClick,
  onCta2Click,
  subtitleRef,
  titleRef,
  contentRef,
  subDescriptionRef,
  ctaRef,
}) => {
  const alignmentClass = center
    ? 'items-center text-center'
    : 'items-start text-left'

  const isRightAlignedButtons = !center && !hasImage && !isFullWidthDescription && !stackActions

  const internalSubtitleRef = useRef<any>(null)
  const internalTitleRef = useRef<any>(null)
  const internalContentRef = useRef<any>(null)
  const internalSubDescriptionRef = useRef<any>(null)
  const internalCtaRef = useRef<any>(null)
  const mobileCtaRef = useRef<any>(null)
  const interactiveRowsRef = useRef<HTMLUListElement>(null)

  const effectiveSubtitleRef = subtitleRef || internalSubtitleRef
  const effectiveTitleRef = titleRef || internalTitleRef
  const effectiveContentRef = contentRef || internalContentRef
  const effectiveSubDescriptionRef = subDescriptionRef || internalSubDescriptionRef
  const effectiveCtaRef = ctaRef || internalCtaRef

  const isLinkCta = ctaVariant === 'link' || (ctaVariant !== 'button' && Boolean(linkText && linkUrl))
  const effectiveLinkText = linkText || (ctaVariant === 'link' ? ctaText : '') || ''
  const effectiveLinkUrl = linkUrl || (ctaVariant === 'link' ? ctaLink : '') || ''

  const isRadarInteractiveRows =
    interactiveRowsVariant === 'radar' ||
    interactiveRowsSize === 'radar' ||
    listLayout === 'interactive-rows-radar' ||
    listVariant === 'interactive-rows-radar'

  const isInteractiveRows =
    isRadarInteractiveRows ||
    listLayout === 'interactive-rows' ||
    listLayout === 'interactive-rows-compact' ||
    listVariant === 'interactive-rows' ||
    listVariant === 'interactive-rows-compact'

  const isCompactInteractiveRows =
    !isRadarInteractiveRows &&
    (interactiveRowsVariant === 'compact' ||
      interactiveRowsSize === 'compact' ||
      listLayout === 'interactive-rows-compact' ||
      listVariant === 'interactive-rows-compact')

  const resolvedInteractiveVariant = isRadarInteractiveRows
    ? 'radar'
    : isCompactInteractiveRows
      ? 'compact'
      : 'default'

  const renderAsGrid =
    (Array.isArray(gridItems) && gridItems.length > 0) ||
    gridItems === true ||
    isGridList ||
    listVariant === 'grid' ||
    listLayout === 'grid' ||
    isInteractiveRows

  const effectiveGridItems = Array.isArray(gridItems) && gridItems.length > 0
    ? gridItems
    : renderAsGrid
      ? listItems
      : undefined

  const hasInteractiveRows = Boolean(isInteractiveRows && effectiveGridItems && effectiveGridItems.length > 0)

  // Entrance animations via GSAP
  useEffect(() => {
    if (!contentOnly) {
      if (!subtitleRef && effectiveSubtitleRef.current) {
        createShowFromBottomAnimation(gsap.timeline(), effectiveSubtitleRef.current)
      }

      if (!titleRef && effectiveTitleRef.current) {
        createShowFromBottomAnimation(gsap.timeline(), effectiveTitleRef.current)
      }

      if (isLinkCta && !ctaRef && effectiveCtaRef.current) {
        createShowFromBottomAnimation(gsap.timeline(), effectiveCtaRef.current)
      }
    }

    if (!titleOnly) {
      if (!contentOnly) {
        if (!contentRef && effectiveContentRef.current) {
          createShowFromBottomAnimation(gsap.timeline(), effectiveContentRef.current)
        }

        if (!subDescriptionRef && effectiveSubDescriptionRef.current) {
          createShowFromBottomAnimation(gsap.timeline(), effectiveSubDescriptionRef.current)
        }

        if (!isLinkCta && !ctaRef && effectiveCtaRef.current) {
          createShowFromBottomAnimation(gsap.timeline(), effectiveCtaRef.current)
        }

        if (!isLinkCta && mobileCtaRef.current) {
          createShowFromBottomAnimation(gsap.timeline(), mobileCtaRef.current)
        }
      }

      if (interactiveRowsRef.current) {
        const rowElements = Array.from(interactiveRowsRef.current.children)
        const rowsTimeline = gsap.timeline()
        rowElements.forEach((el) => {
          createShowFromBottomAnimation(rowsTimeline, el)
        })
      }
    }
  }, [subtitleRef, titleRef, contentRef, subDescriptionRef, ctaRef, contentOnly, titleOnly, isLinkCta])

  // SubDescription width calculation
  let subDescriptionWidth = descriptionWidthClass || 'md:w-6/12'
  if (isFullWidthDescription || (listItems && listItems.length > 0 && isFullWidthSection)) {
    subDescriptionWidth = 'w-full'
  } else if (isFullWidthSection && !isFullWidthDescription) {
    subDescriptionWidth = descriptionWidthClass || 'md:w-6/12'
  }

  // Left column layout class with gap-based spacing (base < md, semi-large md to xl, extra-large xl+)
  const colAlign = center ? 'items-center' : 'items-start'
  let leftColumnClass = `w-full flex flex-col ${colAlign} gap-base md:gap-semi-large xl:gap-extra-large`
  if (isSplitLayout) {
    leftColumnClass = `w-full md:w-1/2 flex flex-col ${colAlign} gap-base md:gap-semi-large xl:gap-extra-large`
  } else if (listItems && listItems.length > 0 && isFullWidthSection) {
    leftColumnClass = `md:w-7/12 flex flex-col ${colAlign} gap-base md:gap-semi-large xl:gap-extra-large`
  }

  // Case 1: Title Only
  if (titleOnly) {
    return (
      <div className={`flex flex-col ${alignmentClass} ${className}`}>
        <SectionHeaderTitle
          subtitle={subtitle}
          titlePart1={titlePart1}
          titlePart2={titlePart2}
          titlePart3={titlePart3}
          isH1={isH1}
          theme={theme}
          isLinkCta={isLinkCta}
          linkText={effectiveLinkText}
          linkUrl={effectiveLinkUrl}
          onCtaClick={onCtaClick}
          subtitleRef={effectiveSubtitleRef}
          titleRef={effectiveTitleRef}
          ctaRef={effectiveCtaRef}
        />
      </div>
    )
  }

  return (
    <div className={`w-full flex flex-col gap-8 lg:gap-12 xl:gap-16 ${alignmentClass} ${className}`}>
      <div
        className={`w-full flex flex-col gap-base md:gap-semi-large xl:gap-extra-large ${
          isSplitLayout || (listItems && listItems.length > 0 && isFullWidthSection)
            ? 'md:flex-row md:items-start md:justify-between md:gap-12 xl:gap-20'
            : ''
        }`}
      >
        {/* LEFT COLUMN: Subtitle, Title, and Description/Actions (when not split) */}
        <div className={leftColumnClass}>
          {!contentOnly && (
            <SectionHeaderTitle
              subtitle={subtitle}
              titlePart1={titlePart1}
              titlePart2={titlePart2}
              titlePart3={titlePart3}
              isH1={isH1}
              theme={theme}
              isLinkCta={isLinkCta && !isRightAlignedButtons}
              linkText={effectiveLinkText}
              linkUrl={effectiveLinkUrl}
              onCtaClick={onCtaClick}
              subtitleRef={effectiveSubtitleRef}
              titleRef={effectiveTitleRef}
              ctaRef={effectiveCtaRef}
            />
          )}

          {!isSplitLayout && (
            isRightAlignedButtons ? (
              <div className="w-full flex flex-col md:flex-row items-start md:items-end justify-between gap-base md:gap-semi-large xl:gap-extra-large">
                <div className={`w-full ${descriptionWidthClass || 'md:w-7/12 xl:w-8/12'}`}>
                  <SectionHeaderDescription
                    description={description}
                    descriptionVariant={descriptionVariant}
                    descriptionClassName={descriptionClassName}
                    descriptionWidthClass="w-full"
                    isFullWidthDescription
                    disableBalance={disableBalance}
                    subDescription={subDescription}
                    subDescriptionClassName={subDescriptionClassName}
                    subDescriptionWidth="w-full"
                    center={center}
                    contentOnly={contentOnly}
                    isSplitLayout={false}
                    contentRef={effectiveContentRef}
                    subDescriptionRef={effectiveSubDescriptionRef}
                  />
                </div>

                {isLinkCta ? (
                  <div className="w-full md:w-auto md:ml-auto flex justify-end shrink-0">
                    <SectionHeaderLinkCta
                      ctaRef={effectiveCtaRef}
                      linkUrl={effectiveLinkUrl}
                      linkText={effectiveLinkText}
                      onCtaClick={onCtaClick}
                      className={contentOnly ? '' : 'opacity-0'}
                    />
                  </div>
                ) : (
                  <div className={`w-full md:w-auto md:ml-auto ${hasInteractiveRows ? 'hidden md:flex' : 'flex'} justify-start md:justify-end shrink-0`}>
                    <SectionHeaderActions
                      ctaText={ctaText}
                      ctaLink={ctaLink}
                      ctaHoverText={ctaHoverText}
                      ctaSize={ctaSize}
                      cta2Text={cta2Text}
                      cta2Link={cta2Link}
                      cta2HoverText={cta2HoverText}
                      cta2Size={cta2Size}
                      onCtaClick={onCtaClick}
                      onCta2Click={onCta2Click}
                      center={center}
                      contentOnly={contentOnly}
                      ctaRef={effectiveCtaRef}
                      isStacked
                    />
                  </div>
                )}
              </div>
            ) : (
              <>
                <SectionHeaderDescription
                  description={description}
                  descriptionVariant={descriptionVariant}
                  descriptionClassName={descriptionClassName}
                  descriptionWidthClass={descriptionWidthClass}
                  isFullWidthDescription={isFullWidthDescription}
                  disableBalance={disableBalance}
                  subDescription={subDescription}
                  subDescriptionClassName={subDescriptionClassName}
                  subDescriptionWidth={subDescriptionWidth}
                  center={center}
                  contentOnly={contentOnly}
                  isSplitLayout={false}
                  contentRef={effectiveContentRef}
                  subDescriptionRef={effectiveSubDescriptionRef}
                />

                {!isLinkCta && (
                  <div className={hasInteractiveRows ? 'hidden md:flex w-full' : 'w-full'}>
                    <SectionHeaderActions
                      ctaText={ctaText}
                      ctaLink={ctaLink}
                      ctaHoverText={ctaHoverText}
                      ctaSize={ctaSize}
                      cta2Text={cta2Text}
                      cta2Link={cta2Link}
                      cta2HoverText={cta2HoverText}
                      cta2Size={cta2Size}
                      onCtaClick={onCtaClick}
                      onCta2Click={onCta2Click}
                      center={center}
                      contentOnly={contentOnly}
                      ctaRef={effectiveCtaRef}
                    />
                  </div>
                )}
              </>
            )
          )}
        </div>

        {/* RIGHT COLUMN: Description and Actions (if split) OR Bullet List */}
        {isSplitLayout ? (
          <div className="w-full md:w-1/2 flex flex-col items-center md:items-start gap-base md:gap-semi-large xl:gap-extra-large">
            <SectionHeaderDescription
              description={description}
              descriptionVariant={descriptionVariant}
              descriptionClassName={descriptionClassName}
              descriptionWidthClass="w-full"
              isFullWidthDescription
              disableBalance={disableBalance}
              subDescription={subDescription}
              subDescriptionClassName={subDescriptionClassName}
              subDescriptionWidth="w-full"
              center={center}
              contentOnly={contentOnly}
              isSplitLayout={true}
              contentRef={effectiveContentRef}
              subDescriptionRef={effectiveSubDescriptionRef}
            />

            {!isLinkCta && (
              <div className={hasInteractiveRows ? 'hidden md:flex w-full' : 'w-full'}>
                <SectionHeaderActions
                  ctaText={ctaText}
                  ctaLink={ctaLink}
                  ctaHoverText={ctaHoverText}
                  ctaSize={ctaSize}
                  cta2Text={cta2Text}
                  cta2Link={cta2Link}
                  cta2HoverText={cta2HoverText}
                  cta2Size={cta2Size}
                  onCtaClick={onCtaClick}
                  onCta2Click={onCta2Click}
                  center={center}
                  contentOnly={contentOnly}
                  ctaRef={effectiveCtaRef}
                />
              </div>
            )}
          </div>
        ) : (
          !effectiveGridItems && listItems && listItems.length > 0 && (
            <div
              className={`${
                isFullWidthSection ? 'md:w-5/12' : 'w-full'
              } flex flex-col mt-8 md:mt-24 md:ml-20`}
            >
              <StyledBulletList
                items={listItems.map((item) => (typeof item === 'string' ? item : item.title))}
                className={center ? 'text-center' : 'text-start'}
              />
            </div>
          )
        )}
      </div>

      {/* BOTTOM SECTION: Interactive Rows OR Grid Cards */}
      {effectiveGridItems && effectiveGridItems.length > 0 && (
        isInteractiveRows ? (
          <>
            <SectionInteractiveRows
              items={effectiveGridItems}
              variant={resolvedInteractiveVariant}
              rowsRef={interactiveRowsRef}
            />
            {!isLinkCta && (ctaText || cta2Text) && (
              <div className="w-full flex md:hidden">
                <SectionHeaderActions
                  ctaText={ctaText}
                  ctaLink={ctaLink}
                  ctaHoverText={ctaHoverText}
                  ctaSize={ctaSize}
                  cta2Text={cta2Text}
                  cta2Link={cta2Link}
                  cta2HoverText={cta2HoverText}
                  cta2Size={cta2Size}
                  onCtaClick={onCtaClick}
                  onCta2Click={onCta2Click}
                  center={center}
                  contentOnly={contentOnly}
                  ctaRef={mobileCtaRef}
                  isStacked={isRightAlignedButtons}
                />
              </div>
            )}
          </>
        ) : (
          <SectionGridCards
            items={effectiveGridItems}
            hideMarker={hideGridMarker}
            textSize={gridTextSize}
            centerText={centerGridText}
            disableHoverOnMobile={disableHoverOnMobile}
            disableHover={disableGridHover}
            center={center}
          />
        )
      )}
    </div>
  )
}

SectionHeader.Title = SectionHeaderTitle
SectionHeader.Description = SectionHeaderDescription
SectionHeader.Actions = SectionHeaderActions
SectionHeader.GridCards = SectionGridCards
SectionHeader.InteractiveRows = SectionInteractiveRows

export default SectionHeader
